import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface CustomSection {
  title: string;
  content: string;
}

export interface ContactSettings {
  pageTitle: string;
  pageSubtitle: string;
  description: string;
  imageUrl: string;
  emailAddress: string;
  phoneNumber: string;
  phoneIcon: string;
  phoneLabel: string;
  responseTime: string;
  recipientEmails: string[];
  customSections: CustomSection[];
  resendApiKey: string;
}

export const defaultContactSettings: ContactSettings = {
  pageTitle: 'Fale Conosco',
  pageSubtitle: 'Tem alguma dúvida, sugestão ou quer falar com a gente? Preencha o formulário abaixo e responderemos o mais rápido possível!',
  description: 'Adoramos ouvir nossos leitores! Seja para tirar dúvidas sobre comportamento felino, sugerir pautas para novos artigos ou compartilhar sua experiência como tutor, estamos sempre de portas abertas.',
  imageUrl: 'https://readdy.ai/api/search-image?query=Adorable%20orange%20tabby%20cat%20sitting%20near%20a%20vintage%20typewriter%20on%20a%20wooden%20desk%20with%20warm%20afternoon%20sunlight%20streaming%20through%20window%2C%20cozy%20home%20office%20atmosphere%2C%20soft%20cream%20and%20amber%20tones%2C%20editorial%20photography%20style%2C%20calm%20and%20inviting%20mood%2C%20high%20detail%2C%20peaceful%20composition&width=600&height=450&seq=contact-cat-cozy-03&orientation=landscape',
  emailAddress: 'contato@miaudecasa.com.br',
  phoneNumber: '',
  phoneIcon: 'ri-whatsapp-line',
  phoneLabel: 'WhatsApp',
  responseTime: 'Respondemos em até 48 horas úteis.',
  recipientEmails: ['contato@miaudecasa.com.br'],
  customSections: [],
  resendApiKey: '',
};

function parseJson(val: unknown, fallback: unknown) {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val as string);
  } catch {
    return fallback;
  }
}

function mapRow(row: Record<string, unknown> | null): ContactSettings {
  if (!row) return JSON.parse(JSON.stringify(defaultContactSettings));
  return {
    pageTitle: (row.page_title as string) ?? defaultContactSettings.pageTitle,
    pageSubtitle: (row.page_subtitle as string) ?? defaultContactSettings.pageSubtitle,
    description: (row.description as string) ?? defaultContactSettings.description,
    imageUrl: (row.image_url as string) ?? defaultContactSettings.imageUrl,
    emailAddress: (row.email_address as string) ?? defaultContactSettings.emailAddress,
    phoneNumber: (row.phone_number as string) ?? defaultContactSettings.phoneNumber,
    phoneIcon: (row.phone_icon as string) ?? defaultContactSettings.phoneIcon,
    phoneLabel: (row.phone_label as string) ?? defaultContactSettings.phoneLabel,
    responseTime: (row.response_time as string) ?? defaultContactSettings.responseTime,
    recipientEmails: parseJson(row.recipient_emails, defaultContactSettings.recipientEmails) as string[],
    customSections: parseJson(row.custom_sections, defaultContactSettings.customSections) as CustomSection[],
    resendApiKey: (row.resend_api_key as string) ?? defaultContactSettings.resendApiKey,
  };
}

export function useContactSettings() {
  const [settings, setSettings] = useState<ContactSettings>(defaultContactSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contact_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (error) throw error;
      setSettings(mapRow(data));
    } catch {
      // keep defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async (updated: ContactSettings): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('contact_settings')
        .upsert({
          id: 1,
          page_title: updated.pageTitle,
          page_subtitle: updated.pageSubtitle,
          description: updated.description,
          image_url: updated.imageUrl,
          email_address: updated.emailAddress,
          phone_number: updated.phoneNumber,
          phone_icon: updated.phoneIcon,
          phone_label: updated.phoneLabel,
          response_time: updated.responseTime,
          recipient_emails: updated.recipientEmails,
          custom_sections: updated.customSections,
          resend_api_key: updated.resendApiKey,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      setSettings(updated);
      return true;
    } catch {
      return false;
    }
  };

  return { settings, saveSettings, loading };
}